import React, { useEffect, useState } from 'react';
import recommend from '@algolia/recommend';
import { useInView } from 'react-intersection-observer';
import { ProjectSettings } from 'shared/types/ProjectSettings';
import SwiperCore, { FreeMode, Navigation, Pagination } from 'swiper';
import { useNotification } from '@/context/notification';
import { SIMILAR_ITEMS } from '@/helpers/constants/algoliaRecommend';
import { lazyloadconfig } from '@/helpers/utils/brandButton';
import { useProjSettings } from '@/providers/projectSettings/hooks/useProjectSettings';
import AlgoliaRecommendProductsList from './algoliaRecommendProductsList';

SwiperCore.use([Pagination, Navigation, FreeMode]);

interface SimilarItemsProps {
  siteId?: string;
  maxSimilarProductsPerPage?: number;
  data: any;
  setProductsHave: (haveProducts: boolean) => void;
}

const SimilarItems: React.FC<SimilarItemsProps> = ({ siteId, maxSimilarProductsPerPage, data, setProductsHave }) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const [lookingSimilarItems, setLookingSimilarItems] = useState<any>(undefined);
  const [inViewState, setInViewState] = useState<boolean>(false);
  const [isPDPPage, setIsPDPPage] = useState<boolean>(false);
  const { setIsSimilarItemVisible } = useNotification();
  const objectID = data?.data?.productListCarousel?.dataSource?.product?.productId ?? data?.data?.nonPdpProductId ?? '';

  useEffect(() => {
    if (window?.location?.href?.indexOf('pdpSimilarItems') > -1) {
      const element = document?.getElementById(SIMILAR_ITEMS);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setInViewState(true);
  }, []);

  useEffect(() => {
    if (window?.location?.href?.indexOf('/product/') > -1) {
      setIsPDPPage(true);
    }
  }, []);

  const { ref, inView } = useInView({
    rootMargin: lazyloadconfig.rootMargin,
    triggerOnce: true,
  });

  useEffect(() => {
    if (!siteId || !projectSettings?.algoliaAppId || !projectSettings?.algoliaApiKey || !objectID) {
      setProductsHave(false);
      return;
    }
    const recommendClient = recommend(projectSettings?.algoliaAppId, projectSettings?.algoliaApiKey);

    if (objectID && recommendClient && (inView || isPDPPage)) {
      let objectId = objectID;
      const isVSku = objectID?.split('-')?.length > 2;
      if (isVSku) {
        const sSku = objectID?.split('-')?.splice(0, 2)?.join('-');
        objectId = sSku;
      }
      const recommendationQueries: any = [
        {
          indexName: `products_${siteId}`,
          objectID: objectId,
          maxRecommendations: maxSimilarProductsPerPage ?? 15,
          queryParameters: {
            filters: 'isInStock:true',
          },
        },
      ];
      recommendClient
        .getLookingSimilar(recommendationQueries)
        .then((recommendations: any) => {
          if (recommendations?.results) {
            const lookingSimilarItems: any[] = [];
            recommendations?.results
              ?.filter((item: any, index: any) => {
                return recommendations?.results?.indexOf(item) === index;
              })
              ?.forEach((item: any) => {
                if (item?.hits && item?.hits.length > 0) {
                  lookingSimilarItems.push(...item.hits);
                }
              });
            setLookingSimilarItems(lookingSimilarItems);
            setIsSimilarItemVisible(lookingSimilarItems.length > 0 ? true : false);
            setProductsHave(lookingSimilarItems?.length > 0);
          }
        })
        .catch((error) => {
          console.error('Error while fetching data:', error);
          setProductsHave(false);
          setIsSimilarItemVisible(false);
          setLookingSimilarItems(undefined);
        });
    }
  }, [objectID, projectSettings, siteId, inView, isPDPPage]);

  if (!lookingSimilarItems?.length) {
    return;
  }
  return (
    <section id="similarItems">
      {isPDPPage ? (
        lookingSimilarItems && <AlgoliaRecommendProductsList item={lookingSimilarItems} />
      ) : (
        <section ref={ref}>
          {lookingSimilarItems && (inView || !inViewState) && (
            <AlgoliaRecommendProductsList item={lookingSimilarItems} />
          )}
        </section>
      )}
    </section>
  );
};

export default SimilarItems;
